/**
 * One recording from start to end (UC-8, light, slice 02), without the browser: the device —
 * the microphone, its recorder and its meter — is handed in, so everything that decides what a
 * recording is can be tested. The slices the recorder hands over every few seconds are kept as
 * they come, so a recording that ends on its own still has what was recorded: when the
 * microphone goes away (a call, a revoked permission), the recorder fails, or the recording
 * reaches the size the server takes, it ends there and is handed on marked *interrupted*. The
 * timer does not count a pause, and the meter stands still in one.
 *
 * It says two things apart: the phase, which changes a few times in a recording, and the readout
 * — the timer and the meter — which moves five times a second.
 */
import type { MicrophoneProblem } from '@/components/humint/questioning/recording-types'
import { type LevelSignal, pushLevel, SILENT_LEVELS } from './level-meter'
import {
  pauseStopwatch,
  resumeStopwatch,
  type Stopwatch,
  startStopwatch,
  stopwatchElapsedMs,
} from './stopwatch'

/** This browser has no recorder here: an old browser, or a page outside a secure context. */
export class RecorderUnsupportedError extends Error {
  constructor() {
    super('This browser cannot record audio here')
    this.name = 'RecorderUnsupportedError'
  }
}

/** What the device says while it records. */
export interface RecorderDeviceEvents {
  /** A slice of the recording, in order. */
  onData: (chunk: Blob) => void
  /** The recorder has ended — asked to, or on its own — and handed over its last slice. */
  onStop: () => void
  /** The microphone went away, or the recorder failed. */
  onLost: () => void
}

/** An open microphone with its recorder and its meter. */
export interface RecorderDevice {
  /** The container it records in, e.g. `audio/webm;codecs=opus`. */
  readonly mimeType: string
  start: (events: RecorderDeviceEvents) => void
  pause: () => void
  resume: () => void
  /** End the recording: the last slice follows, then `onStop`. */
  stop: () => void
  /** The input level right now, 0..1. */
  level: () => number
  /**
   * The true level right now and whether it clips; null where this browser cannot read it. Only a
   * device opened to measure has it (the voice sample's): the questioning's recorder has none.
   */
  signal?: () => LevelSignal | null
  /** Let the microphone and the meter go; no event follows. */
  release: () => void
}

export interface RecorderControllerDeps {
  /** Ask for the microphone; rejects when it is refused or cannot be started. */
  open: () => Promise<RecorderDevice>
  /** A monotonic clock, in ms. */
  now: () => number
  /** The wall clock, ISO. */
  isoNow: () => string
  /** The recording ends on its own once its slices add up to this many bytes. */
  maxBytes: number
  /** How often the timer and the meter move; `RECORDER_TICK_MS` unless a caller counts finer. */
  tickMs?: number
}

export interface RecorderView {
  phase: 'idle' | 'starting' | 'recording' | 'paused'
  /** Why the last start did not record. */
  problem: MicrophoneProblem | null
}

/** What moves while it records. */
export interface RecorderReadout {
  /** Recorded time, pauses left out. */
  elapsedMs: number
  /** The meter's bars, oldest first. */
  levels: readonly number[]
  /**
   * The true level and the clipping sign that came with the newest bar; null where the browser
   * cannot read them. Present only on a device that measures (`RecorderDevice.signal`).
   */
  signal?: LevelSignal | null
}

export const IDLE_RECORDER_VIEW: RecorderView = { phase: 'idle', problem: null }

export const IDLE_RECORDER_READOUT: RecorderReadout = { elapsedMs: 0, levels: SILENT_LEVELS }

/** A recording that has ended; its blob is typed as the container it was recorded in. */
export interface RecordedAudio {
  blob: Blob
  /** When it started, ISO. */
  startedAt: string
  /** Recorded time, pauses left out. */
  durationMs: number
  /** It ended on its own: the microphone went away, the recorder failed, or the size limit. */
  interrupted: boolean
}

export interface RecorderListeners {
  /** The phase changed, or a start gave no recording. */
  onView: (view: RecorderView) => void
  /** The timer and the meter moved: every tick while it records. */
  onReadout: (readout: RecorderReadout) => void
  onRecorded: (audio: RecordedAudio) => void
}

/** How often the timer and the meter move. */
export const RECORDER_TICK_MS = 200

/**
 * How long a recorder asked to end may take to say so. After that the recording is handed on
 * with the slices it has: a recorder that never answers must not hold it for ever.
 */
export const RECORDER_END_GRACE_MS = 2_000

/** Why the microphone gave no recording, from what opening it rejected with. */
function microphoneProblem(error: unknown): MicrophoneProblem {
  if (error instanceof RecorderUnsupportedError) return 'unsupported'
  const name = (error as { name?: unknown } | null)?.name
  return name === 'NotAllowedError' || name === 'SecurityError' ? 'denied' : 'unavailable'
}

interface Session {
  device: RecorderDevice
  chunks: Blob[]
  bytes: number
  startedAt: string
  watch: Stopwatch
  /** `asked`: the operator ended it; `interrupted`: it is ending on its own. */
  ending: 'asked' | 'interrupted' | null
  ticker: ReturnType<typeof setInterval> | null
  grace: ReturnType<typeof setTimeout> | null
}

export class RecorderController {
  private session: Session | null = null
  private view = IDLE_RECORDER_VIEW
  private readout = IDLE_RECORDER_READOUT
  private disposed = false

  constructor(
    private readonly deps: RecorderControllerDeps,
    private readonly listeners: RecorderListeners
  ) {}

  async start(): Promise<void> {
    if (this.disposed || this.view.phase !== 'idle') return
    this.show({ phase: 'starting', problem: null })
    try {
      const device = await this.deps.open()
      // The page went while the browser was asking for the microphone.
      if (this.disposed) device.release()
      else this.begin(device)
    } catch (error) {
      if (!this.disposed) this.show({ phase: 'idle', problem: microphoneProblem(error) })
    }
  }

  pause(): void {
    const { session } = this
    if (!session || session.ending || this.view.phase !== 'recording') return
    session.device.pause()
    session.watch = pauseStopwatch(session.watch, this.deps.now())
    // The timer stands at the instant of the pause, not at the last tick before it.
    this.read({ ...this.readout, elapsedMs: this.elapsedMs(session) })
    this.show({ phase: 'paused' })
  }

  resume(): void {
    const { session } = this
    if (!session || session.ending || this.view.phase !== 'paused') return
    session.device.resume()
    session.watch = resumeStopwatch(session.watch, this.deps.now())
    this.show({ phase: 'recording' })
  }

  /** End the recording; `onRecorded` follows with it. */
  stop(): void {
    this.end('asked')
  }

  /** The page goes: the microphone is let go and nothing more is said. */
  dispose(): void {
    this.disposed = true
    if (this.session) this.close(this.session)
  }

  private show(patch: Partial<RecorderView>): void {
    this.view = { ...this.view, ...patch }
    this.listeners.onView(this.view)
  }

  private read(readout: RecorderReadout): void {
    this.readout = readout
    this.listeners.onReadout(readout)
  }

  /** Whole milliseconds: the browser's clock counts in fractions of one. */
  private elapsedMs(session: Session): number {
    return Math.round(stopwatchElapsedMs(session.watch, this.deps.now()))
  }

  private begin(device: RecorderDevice): void {
    const session: Session = {
      device,
      chunks: [],
      bytes: 0,
      startedAt: this.deps.isoNow(),
      watch: startStopwatch(this.deps.now()),
      ending: null,
      ticker: null,
      grace: null,
    }
    this.session = session
    try {
      device.start({
        onData: this.keep,
        onStop: this.finish,
        onLost: () => this.end('interrupted'),
      })
    } catch (error) {
      // A recorder that would not start holds no microphone and ticks no timer.
      this.close(session)
      throw error
    }
    session.ticker = setInterval(this.tick, this.deps.tickMs ?? RECORDER_TICK_MS)
    this.read(IDLE_RECORDER_READOUT)
    this.show({ phase: 'recording', problem: null })
  }

  private readonly tick = (): void => {
    const { session } = this
    if (!session || session.ending || this.view.phase !== 'recording') return
    const { device } = session
    this.read({
      elapsedMs: this.elapsedMs(session),
      levels: pushLevel(this.readout.levels, device.level()),
      // Only a device that measures: the questioning's readout is its timer and its bars.
      ...(device.signal && { signal: device.signal() }),
    })
  }

  private readonly keep = (chunk: Blob): void => {
    const { session } = this
    if (!session) return
    session.chunks.push(chunk)
    session.bytes += chunk.size
    if (session.bytes >= this.deps.maxBytes) this.end('interrupted')
  }

  /** The timer stops here; the recorder's last slice and its `onStop` are still to come. */
  private end(ending: 'asked' | 'interrupted'): void {
    const { session } = this
    if (!session || session.ending) return
    session.ending = ending
    session.watch = pauseStopwatch(session.watch, this.deps.now())
    this.read({ ...this.readout, elapsedMs: this.elapsedMs(session) })
    session.grace = setTimeout(this.finish, RECORDER_END_GRACE_MS)
    session.device.stop()
  }

  /** The recorder ended: what it recorded is handed on, marked when nobody asked it to end. */
  private readonly finish = (): void => {
    const { session } = this
    if (!session) return
    this.close(session)
    this.read(IDLE_RECORDER_READOUT)
    this.show(IDLE_RECORDER_VIEW)
    this.listeners.onRecorded({
      blob: new Blob(session.chunks, { type: session.device.mimeType }),
      startedAt: session.startedAt,
      durationMs: this.elapsedMs(session),
      interrupted: session.ending !== 'asked',
    })
  }

  private close(session: Session): void {
    this.session = null
    if (session.ticker) clearInterval(session.ticker)
    if (session.grace) clearTimeout(session.grace)
    session.device.release()
  }
}
